import { useEffect, useState } from "react";
import { Button } from "reactstrap";
import AppSpinner from "../../../elements/AppSpinner";
import AppDrawer, { DrawerError } from "../AppDrawer";
import InstanceUtils from "../../../utils/InstanceUtils";
import BrieRecordAssist from "../../../views/brie/BrieAssist/BrieRecordAssist";
import { HandleUpdateBrie } from "./CreateRecordDrawer";
import { LayoutAndSections } from "../../../types/TenantInterfaces";
import useModule from "../../../hooks/useModule";
import useRecord from "../../../api/api-hooks/records/useRecord";
import useUpdateRecord from "../../../api/api-hooks/records/useUpdateRecord";
import FieldUtils from "../../../utils/FieldUtils";
import CreateRecordRendererV2 from "../../Records/CreateRecordRendererV2";

interface Props {
  open: boolean;
  onClose: () => void;
  recordID: string;
  moduleAPIName?: string;
  layout: LayoutAndSections | null;
}

const UpdateRecordDrawer = ({
  open,
  onClose,
  recordID,
  moduleAPIName,
  layout,
}: Props) => {
  const { module } = useModule(moduleAPIName, true);
  const { record } = useRecord({ moduleAPIName, recordID, enabled: open });
  const { updateRecord, recordUpdating, updateRecordError } = useUpdateRecord(
    false,
    recordID
  );
  const [values, setValues] = useState<any>(null);
  const [prefill, setPrefill] = useState<any>(null);
  const [containsRequired, setContainsRequired] = useState(false);
  const [error, setError] = useState<DrawerError | null>(null);

  const handleChange = (newValues: any) => {
    setValues(newValues);
  };

  const handleSave = async () => {
    if (!containsRequired || !moduleAPIName) return;

    const payload = { ...values };

    // Filter out values that were not changed
    for (const valueKey of Object.keys(payload)) {
      if (!payload[valueKey] && !record[valueKey]) {
        delete payload[valueKey];
      } else if (payload[valueKey] === record[valueKey]) {
        delete payload[valueKey];
      } else if (typeof payload[valueKey] === "object") {
        try {
          if (
            JSON.stringify(payload[valueKey]) ===
            JSON.stringify(record[valueKey])
          ) {
            delete payload[valueKey];
          }
        } catch {}
      }
    }

    let formattedPayload = await InstanceUtils.formatRecordCreationPayload(
      module?.Fields,
      payload
    );

    if (!Object.keys(formattedPayload || {}).length) {
      return setError({
        message: "No fields have been updated!",
        onClear: () => setError(null),
      });
    }

    updateRecord({
      field: { ReadOnly: false },
      moduleAPIName: moduleAPIName,
      recordID: recordID,
      payload: formattedPayload,
      onSuccess: onClose,
      _UserActivity: undefined,
    });
  };

  // Sync contains required with values
  useEffect(() => {
    // @ts-ignore
    setContainsRequired(FieldUtils.containsRequiredFields(module, values));
  }, [module, values]);

  // Set up prefill so the existing record values are filled into the form.
  useEffect(() => {
    if (!record || !module?.Fields) return;

    const constructedPrefill = [];

    for (const field of module?.Fields ?? []) {
      if (record[field.APIName]) {
        constructedPrefill.push({
          Field: field.APIName,
          Value: record[field.APIName],
        });
      }
    }

    setPrefill(constructedPrefill);
  }, [record, module?.Fields]);

  return (
    <AppDrawer
      open={open}
      onClose={onClose}
      headerTitle="Update Record"
      headerOptions={
        <div className="d-flex align-items-center">
          {module && (
            <BrieRecordAssist
              module={module}
              onConfirm={(result) =>
                HandleUpdateBrie(result, prefill, setPrefill, values, setValues)
              }
            />
          )}
          <Button
            style={{ width: 100 }}
            size="sm"
            color="primary"
            onClick={handleSave}
            disabled={!containsRequired || recordUpdating}
          >
            {recordUpdating ? <AppSpinner size="sm" color="white" /> : "Save"}
          </Button>
        </div>
      }
      bodyCSS={"pt-2 pb-8"}
      error={error}
      body={
        <CreateRecordRendererV2
          module={module}
          onChange={handleChange}
          prefill={prefill}
          disableReadonlyFields={true}
          layout={layout}
          variableValues={undefined}
        />
      }
    />
  );
};

export default UpdateRecordDrawer;
